import {
  Box,
  Flex,
  HStack,
  type StackProps,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import type { Maybe, User } from "@src/__generated__/graphql";
import { Avatar, type UserTooltipProps } from "@src/components/ui-kit";
import { BudgetIcon, TrackedTimeIcon } from "@src/components/ui-kit/Icon";
import { useStore } from "@src/utils/hooks";
import { userScopeToType } from "@src/utils/userScopeToType";
import type { FunctionComponent } from "react";
import { timeToHrs } from "./ModalCommunication";

export type PositionBadgeUser = Maybe<
  Partial<UserTooltipProps["user"]> & {
    first_name?: string | null;
    last_name?: string | null;
    scope?: User["scope"];
  }
>;
type PositionBadgeWithHoursProps = {
  user?: PositionBadgeUser;
  workTypeTitle?: string;
  reportedValue?: string | number;
  plannedValue?: string | number;
} & StackProps;

export const PositionBadgeWithHours: FunctionComponent<
  PositionBadgeWithHoursProps
> = ({ reportedValue, plannedValue, user, workTypeTitle, ...props }) => {
  const userType = userScopeToType(user?.scope?.value);
  const { taskDetailModalStore } = useStore();

  return (
    <Tooltip
      label={
        !user?.scope && (
          <Box>
            <Box>{`${user?.full_name} / ${workTypeTitle}`}</Box>
            <Box>
              <Trans>Tracked:</Trans>&nbsp;
              {timeToHrs(Number(reportedValue))}
              <Trans>h</Trans>
            </Box>
            <Box>
              <Trans>Budgeted in task:</Trans>&nbsp;
              {timeToHrs(Number(plannedValue))}
              <Trans>h</Trans>
            </Box>
          </Box>
        )
      }
    >
      <HStack
        h="8"
        pr="3"
        pl="1"
        color={
          taskDetailModalStore.isOvertime(reportedValue, Number(plannedValue))
            ? "red.800"
            : ""
        }
        bg={
          taskDetailModalStore.isOvertime(reportedValue, Number(plannedValue))
            ? "red.100"
            : ""
        }
        borderWidth="1px"
        borderColor="grey.100"
        rounded="full"
        spacing="2"
        {...props}
      >
        <Box>
          <Avatar
            name={user?.full_name}
            src={user?.image?.urls.thumbnail}
            size="xs"
            colorScheme={user?.profile?.hex_color}
          />
        </Box>

        {user?.first_name && user?.last_name && (
          <Text>
            {user.first_name[0]}.&nbsp;{user.last_name}
          </Text>
        )}

        {!user?.scope ? (
          <Flex>
            <HStack spacing="3px">
              <TrackedTimeIcon boxSize="3" />
              <Text fontSize="xs" fontWeight="medium">
                <Trans>{timeToHrs(Number(reportedValue))}h</Trans>
              </Text>
            </HStack>
            <Text color="grey.500" fontSize="xs" fontWeight="medium">
              &nbsp; / &nbsp;
            </Text>
            <HStack spacing="3px">
              <BudgetIcon boxSize="3" />
              <Text fontSize="xs" fontWeight="medium">
                <Trans>{timeToHrs(Number(plannedValue))}h</Trans>
              </Text>
            </HStack>
          </Flex>
        ) : (
          <Text fontSize="xs" fontWeight="medium">
            ({userType})
          </Text>
        )}
      </HStack>
    </Tooltip>
  );
};
